import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from 'react-router-dom'
import { AuthProvider } from '@/contexts/AuthContextSupabase'
import { LanguageProvider } from '@/contexts/LanguageContext'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { MainLayout } from '@/components/layout/MainLayout'
import { ProgressSpinner } from 'primereact/progressspinner'
import styles from './App.module.css'

// Lazy load all pages for code splitting
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
)
const HomePage = lazy(() =>
  import('@/pages/HomePage').then((m) => ({ default: m.HomePage }))
)
const OrderPage = lazy(() =>
  import('@/pages/Order').then((m) => ({ default: m.OrderPage }))
)
const OrderNewPage = lazy(() =>
  import('@/pages/OrderNewPage').then((m) => ({ default: m.OrderNewPage }))
)
const OrderNewPage2 = lazy(() =>
  import('@/pages/OrderNewPage2').then((m) => ({ default: m.OrderNewPage2 }))
)
const AreaMasterPage = lazy(() =>
  import('@/pages/AreaMasterPage').then((m) => ({ default: m.AreaMasterPage }))
)
const InquirySearchPage = lazy(() =>
  import('@/pages/InquirySearchPage').then((m) => ({
    default: m.InquirySearchPage,
  }))
)
const SalesReportPage = lazy(() =>
  import('@/pages/SalesReportPage').then((m) => ({
    default: m.SalesReportPage,
  }))
)
const StaffMasterPage = lazy(() =>
  import('@/pages/StaffMasterPage').then((m) => ({
    default: m.StaffMasterPage,
  }))
)
const CategoryMasterPage = lazy(() =>
  import('@/pages/CategoryMasterPage').then((m) => ({
    default: m.CategoryMasterPage,
  }))
)

// Loading fallback component
function LoadingFallback() {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingContent}>
        <ProgressSpinner
          style={{ width: '60px', height: '60px' }}
          strokeWidth="4"
        />
        <p className={styles.loadingText}>読み込み中...</p>
      </div>
    </div>
  )
}

// Root layout with providers
function RootLayout() {
  return (
    <div className="jp-theme">
      <AuthProvider>
        <LanguageProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Outlet />
          </Suspense>
        </LanguageProvider>
      </AuthProvider>
    </div>
  )
}

// Protected layout wrapper
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  )
}

// Create the data router with route configuration
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/',
        element: <ProtectedLayout />,
        children: [
          {
            index: true,
            element: <HomePage />,
          },
          {
            path: 'orders',
            element: <OrderPage />,
          },
          {
            path: 'order-new',
            element: <OrderNewPage />,
          },
          {
            path: 'order-new/:orderId',
            element: <OrderNewPage />,
          },
          {
            path: 'order-new-v2',
            element: <OrderNewPage2 />,
          },
          {
            path: 'order-new-v2/:orderId',
            element: <OrderNewPage2 />,
          },
          {
            path: 'inquiries',
            element: <InquirySearchPage />,
          },
          {
            path: 'reports-sales',
            element: <SalesReportPage />,
          },
          {
            path: 'staffs',
            element: <StaffMasterPage />,
          },
          {
            path: 'categories',
            element: <CategoryMasterPage />,
          },
          {
            path: 'areas',
            element: <AreaMasterPage />,
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
])

function App() {
  return <RouterProvider router={router} />
}

export default App
